import { useMemo, useState, useEffect, useContext } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  useMediaQuery,
  Divider,
  Typography,
  Theme,
  FormControlLabel,
  Switch
} from '@mui/material';
import { ISortDirection, IBuildDocumentation } from '../../../models';
import makeStyles from '@mui/styles/makeStyles';
import { getBuildDocumentation } from '../../../fetch';
import { MobileTable, Pagination, Table, Page, PageTitle } from '../../../components';
import { formatDate, getEnvironment, handleSortDirection, isExternalUser } from '../../../helpers';
import { BuildDocumentationFilters } from './build-documentation-filters';
import { DocumentStatusIcons } from './document-status-icons';
import { useHistory } from 'react-router-dom';
import {
  filtersPayload,
  sortingPayload,
  updateFilters,
  updateSort
} from '../../../context/actions';
import { GlobalContext } from '../../../context/global';
import { updateGlobalPerPage } from '../../../context/reducers/pagingReducer';
import { UserContext } from '../../../context';

interface IBuildDocumentationFilters {
  filters?: {
    neighborhoodId?: string;
    role?: string;
    status?: string;
    searchText?: string;
    closingDateLocked?: string;
    termiteLetterUploaded?: string;
    coUploaded?: string;
  };
}
interface IBuildDocumentationSorting {
  sort?: string;
  sortDirection?: { [key: string]: ISortDirection };
}

const PAGE_NAME = 'build-documentation';

export const BuildDocumentation = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const { enqueueSnackbar } = useSnackbar();

  const [recordCount, setRecordCount] = useState<number>(0);
  const [excludeHiddenBuilds, setExcludeHiddenBuilds] = useState<boolean>(true);

  const { filterValues, dispatchFilters, sortingValues, dispatchSorting } =
    useContext(GlobalContext);

  const user = useContext(UserContext);

  const currentFilterState = filterValues.find(state => state?.pageName === PAGE_NAME);

  const persistentFilters = useMemo<IBuildDocumentationFilters>(
    () =>
      currentFilterState
        ? (currentFilterState as IBuildDocumentationFilters)
        : ({} as IBuildDocumentationFilters),
    [currentFilterState]
  );

  const currentSortingState = sortingValues.find(state => state?.pageName === PAGE_NAME);

  const persistentSorting = useMemo<IBuildDocumentationSorting>(
    () =>
      currentSortingState
        ? (currentSortingState as IBuildDocumentationSorting)
        : ({} as IBuildDocumentationSorting),
    [currentSortingState]
  );

  const handleUpdateFilters = (payload: filtersPayload) => {
    dispatchFilters(updateFilters(payload));
  };
  const handleUpdateSorting = (payload: sortingPayload) => {
    dispatchSorting(updateSort(payload));
  };

  const selectedSort = useMemo<string>(
    () => persistentSorting.sort ?? 'closingDate',
    [persistentSorting.sort]
  );
  const sortDirection = useMemo<{
    neighborhood?: ISortDirection;
    lotNumber?: ISortDirection;
    plan?: ISortDirection;
    client?: ISortDirection;
    formDate?: ISortDirection;
    closingDate?: ISortDirection;
  }>(
    () =>
      persistentSorting?.sortDirection && Object.keys(persistentSorting?.sortDirection).length > 0
        ? persistentSorting?.sortDirection
        : { closingDate: 'Asc' },
    [persistentSorting.sortDirection]
  );

  const [isLoadingBuilds, setLoadingBuilds] = useState(false);
  const [builds, setBuilds] = useState<IBuildDocumentation[]>([]);

  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>(
    persistentFilters?.filters?.neighborhoodId ?? ''
  );

  const [selectedStatus, setSelectedStatus] = useState<string>(
    persistentFilters?.filters?.status ?? ''
  );

  const [selectedRole, setSelectedRole] = useState<string>(persistentFilters?.filters?.role ?? '');

  const [searchText, setSearchText] = useState<string>(
    persistentFilters?.filters?.searchText ?? ''
  );

  const [selectedClosingDateLocked, setSelectedClosingDateLocked] = useState<string>(
    persistentFilters?.filters?.closingDateLocked ?? ''
  );
  const [selectedTermiteLetterUploaded, setSelectedTermiteLetterUploaded] = useState<string>(
    persistentFilters?.filters?.termiteLetterUploaded ?? ''
  );
  const [selectedCOUploaded, setSelectedCOUploaded] = useState<string>(
    persistentFilters?.filters?.coUploaded ?? ''
  );

  const [page, setPage] = useState<number>(0);

  const { perPageGlobalValues, dispatchGlobalPerPage } = useContext(GlobalContext);
  const currentPageState = perPageGlobalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;

  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 5 ? currentPageState : 10),
    [currentPageState]
  );
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(
    Object.keys(persistentFilters).length !== 0
  );

  const fetchDocumentationBuilds = async () => {
    try {
      setLoadingBuilds(true);
      const res = await getBuildDocumentation({
        buildStateFilter: selectedStatus,
        neighborhoodId: selectedNeighborhoodId,
        roleFilter: selectedRole,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        page: page + 1,
        perPage: perPage,
        excludeHiddenBuilds: excludeHiddenBuilds,
        closingDateLocked: selectedClosingDateLocked,
        termiteLetterUploaded: selectedTermiteLetterUploaded,
        coUploaded: selectedCOUploaded,
        searchText: searchText
      });
      setRecordCount(res.totalRecordCount);
      setBuilds(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading builds, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingBuilds(false);
    }
  };

  useEffect(() => {
    if (selectedStatus) {
      fetchDocumentationBuilds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort, persistentFilters, page, perPage, excludeHiddenBuilds]);

  const history = useHistory();
  const handleEdit = (original: IBuildDocumentation) => {
    history.push(`/documentation/build-documentation/${original.buildId}`);
  };

  const data = useMemo(() => builds ?? [], [builds]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      const directionOfSort = handleSortDirection(sortDirection?.[column]);
      handleUpdateSorting({
        sort: directionOfSort ? column : '',
        sortDirection: {
          [column]: directionOfSort
        },
        pageName: PAGE_NAME,
        environment: getEnvironment()
      });
    };
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodDescription',
        isServerSorted: selectedSort === 'neighborhood',
        isServerSortedDesc: sortDirection.neighborhood === 'Desc',
        handleClickColumn: () => handleClickColumn('neighborhood')
      },
      {
        Header: 'Lot Number',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'lotNumber',
        isServerSortedDesc: sortDirection.lotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('lotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'planDescription',
        isServerSorted: selectedSort === 'plan',
        isServerSortedDesc: sortDirection.plan === 'Desc',
        handleClickColumn: () => handleClickColumn('plan')
      },
      {
        Header: 'Purchaser',
        accessor: 'contractName',
        isServerSorted: selectedSort === 'client',
        isServerSortedDesc: sortDirection.client === 'Desc',
        handleClickColumn: () => handleClickColumn('client')
      },
      {
        Header: 'Closing Date',
        accessor: 'closingDate',
        isServerSorted: selectedSort === 'closingDate',
        isServerSortedDesc: sortDirection.closingDate === 'Desc',
        handleClickColumn: () => handleClickColumn('closingDate'),

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuildDocumentation } };
        }) => {
          return <>{original.closingDate ? formatDate(new Date(original.closingDate)) : null}</>;
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuildDocumentation } };
        }) => (
          <DocumentStatusIcons
            buildDocumentation={original}
            showEditButton={true}
            onEdit={() => handleEdit(original)}
          />
        ),
        // Mobile-specific cell that only shows the document icons
        MobileCell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuildDocumentation } };
        }) => <DocumentStatusIcons buildDocumentation={original} />
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort]);
  return (
    <>
      <Page title="Build Documentation">
        <PageTitle
          title="Build Documentation"
          additionalHeaderContent={
            !isExternalUser(user.user) && (
              <FormControlLabel
                control={
                  <Switch
                    checked={excludeHiddenBuilds}
                    onChange={() => setExcludeHiddenBuilds(!excludeHiddenBuilds)}
                    name="excludeHiddenBuilds"
                    color="primary"
                  />
                }
                label="Hide Excluded Builds"
              />
            )
          }
        />
        <BuildDocumentationFilters
          isLoading={isLoadingBuilds}
          selectedNeighborhoodId={selectedNeighborhoodId}
          setSelectedNeighborhoodId={setSelectedNeighborhoodId}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          searchText={searchText}
          setSearchText={setSearchText}
          selectedClosingDateLocked={selectedClosingDateLocked}
          setSelectedClosingDateLocked={setSelectedClosingDateLocked}
          selectedTermiteLetterUploaded={selectedTermiteLetterUploaded}
          setSelectedTermiteLetterUploaded={setSelectedTermiteLetterUploaded}
          selectedCOUploaded={selectedCOUploaded}
          setSelectedCOUploaded={setSelectedCOUploaded}
          fetchBuilds={fetchDocumentationBuilds}
          setHasAppliedFilters={setHasAppliedFilters}
          hasAppliedFilters={hasAppliedFilters}
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              const payload = {
                filters: {
                  neighborhoodId: '',
                  role: '',
                  status: '',
                  searchText: '',
                  closingDateLocked: '',
                  termiteLetterUploaded: '',
                  coUploaded: ''
                },
                pageName: PAGE_NAME,
                environment: getEnvironment()
              };
              handleUpdateFilters(payload);
            } else {
              setPage(0);
              const payload = {
                ...persistentFilters,
                filters: {
                  neighborhoodId: selectedNeighborhoodId,
                  status: selectedStatus,
                  role: selectedRole,
                  searchText: searchText,
                  closingDateLocked: selectedClosingDateLocked,
                  termiteLetterUploaded: selectedTermiteLetterUploaded,
                  coUploaded: selectedCOUploaded
                },
                pageName: PAGE_NAME,
                environment: getEnvironment()
              };
              handleUpdateFilters(payload);
            }
          }}
        />
        {hasAppliedFilters && (
          <>
            <Table
              columns={columns}
              data={data}
              isLoading={isLoadingBuilds}
              hidePagination
              mobileProps={{
                getAccordionHeader: (original: IBuildDocumentation) =>
                  `${original.neighborhoodDescription} - Lot ${original.lotNumber}`,
                fields: columns
                  .filter(c => c.Header || c.id === 'actions')
                  .map(c => ({
                    name: c.Header,
                    accessor: c.accessor,
                    Cell: c.id === 'actions' ? c?.MobileCell : c?.Cell
                  })),
                handleEdit: handleEdit
              }}
              ResponsiveComponent={isMobile ? MobileTable : undefined}
            />
            {!isLoadingBuilds && (
              <Pagination
                page={page}
                count={recordCount}
                rowsPerPage={perPage}
                setPage={setPage}
                setRowsPerPage={itemsPerPage => {
                  return dispatchGlobalPerPage(
                    updateGlobalPerPage({
                      pageName: PAGE_NAME,
                      environment: getEnvironment(),
                      itemsPerPage: itemsPerPage
                    })
                  );
                }}
              />
            )}
          </>
        )}
        {!hasAppliedFilters && (
          <>
            <Divider className={classes.divider} />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="5rem"
            >
              <Typography>Please select a Status to view builds.</Typography>
            </Box>
          </>
        )}
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    margin: `${theme.spacing(1)} 0`
  }
}));
